<template>
  <div class="language-dropdown-container">
    <label class="language-label">{{ $t('common.platformLanguage') }}: </label>
    <UISelect
      :options="supportedLanguages"
      :value="selectedLanguage"
      @change="value => setPreferredLanguage(value)"
      class="removeShadow"
      :style="{ width: setWidthFromSelectedValue, maxWidth: '198px'}"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { Languages, updateLocale, localizationKey } from '../../../localization'
import { Modules } from '../../../util/modules'

interface LanguageOption {
  label: string
  value: Languages
}

// set language based on location
const defaultLanguage = 'en-US'

const languageMap: Record<string, Languages> = {
  en: Languages.English_US,
  da: Languages.Danish,
  de: Languages.German,
  es: Languages.Spanish,
  fi: Languages.Finnish,
  'fr-CA': Languages.French_Canada,
  'fr-FR': Languages.French_France,
  it: Languages.Italian,
  nl: Languages.Dutch,
  no: Languages.Norwegian,
  'pt-BR': Languages.Portuguese_Brazil,
  'pt-PT': Languages.Portuguese_Portugal,
  sv: Languages.Swedish,
}

export default defineComponent({
  emits: ['update-selected-language'],
  data() {
    return {
      supportedLanguages: [
        {
          label: 'English',
          value: Languages.English_US,
        },
        {
          label: 'Dansk',
          value: Languages.Danish,
        },
        {
          label: 'Deutsch',
          value: Languages.German,
        },
        {
          label: 'Español',
          value: Languages.Spanish,
        },
        {
          label: 'Suomi',
          value: Languages.Finnish,
        },
        {
          label: 'Français (Canada)',
          value: Languages.French_Canada,
        },
        {
          label: 'Français (France)',
          value: Languages.French_France,
        },
        {
          label: 'Italiano',
          value: Languages.Italian,
        },
        {
          label: 'Nederlands',
          value: Languages.Dutch,
        },
        {
          label: 'Norsk',
          value: Languages.Norwegian,
        },
        {
          label: 'Português (Brasil)',
          value: Languages.Portuguese_Brazil,
        },
        {
          label: 'Português (Portugal)',
          value: Languages.Portuguese_Portugal,
        },
        {
          label: 'Svenska',
          value: Languages.Swedish,
        },
      ] as LanguageOption[],
      selectedLanguage: Languages.English_US as Languages,
    }
  },
  computed: {
    setWidthFromSelectedValue(): string {
      const selectedOption = this.supportedLanguages.find(lang => lang.value === this.selectedLanguage)
      if (selectedOption) {
        return `calc(${selectedOption.label.length + 7.5}ch) !important`
      }
      return 'auto !important'
    },
  },
  mounted() {
    this.setLanguageBasedOnLocation()
  },
  methods: {
    setLanguageBasedOnLocation() {

      const storedLanguage = localStorage.getItem(localizationKey)
      if (storedLanguage) {
        this.selectedLanguage = storedLanguage as Languages
        this.$emit('update-selected-language', storedLanguage as Languages)
        return
      }

      const languagesList = Array.isArray(navigator.languages) ? navigator.languages : []
      const rawBrowserLanguage = (navigator.language || languagesList[0] || defaultLanguage) as string

      // Normalize to handle case/format differences like "en-US" vs "en_US"
      const normalized = rawBrowserLanguage.replace('_', '-').toLowerCase()
      const languageCode = normalized.substring(0, 2)

      // Prefer full locale match, then 2-letter code match, then default
      const languageToSet =
        languageMap[normalized] ||
        languageMap[languageCode] ||
        languageMap[defaultLanguage.toLowerCase()] ||
        Languages.English_US
      this.setPreferredLanguage(languageToSet)
    },
    setPreferredLanguage(value: string) {
      updateLocale((value as Languages) || Languages.English_US, [Modules.Login])
        .then(() => {
          this.selectedLanguage = value as Languages
          this.$emit('update-selected-language', value as Languages)
        })
        .catch(err => {
          console.error(err)
        })
    },
  },
})
</script>

<style scoped>
.language-dropdown-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.language-label {
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0%;
}

.removeShadow:focus {
  box-shadow: none;
}

.removeShadow :deep(select) {
  border-radius: 0 !important;
}
</style>
